import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import AppShell from "../../components/layout/AppShell";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import {
  ArrowLeft, Coins, Wallet, Users, Receipt, Copy, ArrowsClockwise, CurrencyInr,
} from "@phosphor-icons/react";

export default function CustomerRewards() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [payoutOpen, setPayoutOpen] = useState(false);

  const load = async () => {
    try { setData((await api.get(`/customers/${id}/rewards`)).data); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const copyLink = async () => {
    try {
      const r = await api.get(`/customers/${id}/referral-link`);
      const url = r.data.url.startsWith("/")
        ? `https://rescigo.in${r.data.url}` : r.data.url;
      await navigator.clipboard.writeText(url);
      toast.success(`Link copied: ${url}`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!data) return <AppShell title="Rewards & Disbursement"><div className="p-6 text-sm text-[#5C5A55]">Loading…</div></AppShell>;
  const w = data.wallet;

  return (
    <AppShell title={`Rewards · ${data.customer.name}`} subtitle={`${data.customer.customer_code} · 10% on first package payment`}
      actions={
        <div className="flex gap-2">
          <button onClick={() => nav(`/customers/${id}`)} className="h-10 px-3 inline-flex items-center gap-2 border border-[#E5E2D9] rounded-md text-sm">
            <ArrowLeft size={14}/>Back to profile
          </button>
          <button onClick={copyLink} data-testid="rewards-copy-link"
                  className="h-10 px-3 inline-flex items-center gap-2 border border-[#2D5A27] text-[#2D5A27] hover:bg-[#EFF3EE] rounded-md text-sm">
            <Copy size={14}/>Copy referral link
          </button>
          <button onClick={() => setPayoutOpen(true)} disabled={!data.payout_enabled} data-testid="rewards-payout-btn"
                  title={data.payout_enabled ? "" : `Available must be ≥ ₹${data.payout_min}`}
                  className="h-10 px-4 bg-[#2D5A27] hover:bg-[#3A7033] disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-md text-sm font-medium inline-flex items-center gap-2">
            <Coins size={14} weight="bold"/>Manual Payout
          </button>
        </div>
      }>
      <div className="grid grid-cols-4 gap-4 mb-6" data-testid="rewards-stats">
        <Stat label="Total Earned" value={`₹${w.total_earned.toFixed(2)}`} icon={Wallet}/>
        <Stat label="Available Balance" value={`₹${w.available.toFixed(2)}`} icon={Coins} accent={data.payout_enabled}/>
        <Stat label="Total Paid" value={`₹${w.total_paid.toFixed(2)}`} icon={CurrencyInr}/>
        <Stat label="Pending" value={`₹${(w.pending || 0).toFixed(2)}`}/>
      </div>

      <div className="grid grid-cols-2 gap-5">
        <Section title="Referred Customers" testid="ref-customers" count={data.referred_customers.length}>
          {data.referred_customers.length === 0 ? <Empty>None yet — share the referral link.</Empty> :
            data.referred_customers.map(c => (
              <div key={c.id} className="px-5 py-2 border-b border-[#F0ECE3] flex items-center gap-2 text-sm">
                <span className="font-medium flex-1">{c.name}</span>
                <span className="font-mono text-xs text-[#5C5A55]">{c.customer_code}</span>
                <span className="text-xs text-[#8A8780]">{(c.created_at || "").slice(0,10)}</span>
              </div>
            ))}
        </Section>

        <Section title="Reward History" testid="ref-history" count={data.referrals.length} icon={Receipt}>
          {data.referrals.length === 0 ? <Empty>No rewards credited yet.</Empty> :
            data.referrals.map(r => (
              <div key={r.id} className="px-5 py-2 border-b border-[#F0ECE3] text-sm">
                <div className="flex items-center gap-2">
                  <span className="font-medium flex-1">{r.referred_customer_name}</span>
                  <span className="font-mono text-[#2D5A27]">+₹{r.reward_amount.toFixed(2)}</span>
                </div>
                <div className="text-xs text-[#8A8780] flex items-center gap-2">
                  <Link to={`/finance/invoices`} className="font-mono hover:underline">{r.invoice_no}</Link>
                  <span>·</span>
                  <span>{(r.rewarded_at || "").slice(0,10)}</span>
                </div>
              </div>
            ))}
        </Section>

        <Section title="Payout History" testid="ref-payouts" count={data.payouts.length} icon={Coins}>
          {data.payouts.length === 0 ? <Empty>No payouts yet.</Empty> :
            data.payouts.map(p => (
              <div key={p.id} className="px-5 py-2 border-b border-[#F0ECE3] text-sm">
                <div className="flex items-center gap-2">
                  <span className="px-2 py-0.5 rounded text-[10px] uppercase tracking-[0.18em] bg-[#D4E0D1] text-[#2D5A27]">{p.method}</span>
                  <span className="font-mono text-xs text-[#5C5A55] flex-1">{p.upi_txn_id || "—"}</span>
                  <span className="font-mono text-[#C25D38]">−₹{p.amount.toFixed(2)}</span>
                </div>
                <div className="text-xs text-[#8A8780]">{p.paid_date} · by {p.paid_by_name}</div>
              </div>
            ))}
        </Section>

        <Section title="Wallet Ledger" testid="ref-ledger" count={data.ledger.length} icon={ArrowsClockwise}>
          {data.ledger.length === 0 ? <Empty>No activity.</Empty> :
            data.ledger.map(l => (
              <div key={l.id} className="px-5 py-2 border-b border-[#F0ECE3] text-sm flex items-center gap-2">
                <span className={`px-2 py-0.5 rounded text-[10px] uppercase tracking-[0.18em] ${l.type === "credit" ? "bg-[#D4E0D1] text-[#2D5A27]" : "bg-[#F4C7B9] text-[#7A2E18]"}`}>
                  {l.type}
                </span>
                <span className="text-xs text-[#5C5A55] flex-1">{l.source}{l.invoice_no ? ` · ${l.invoice_no}` : ""}{l.method ? ` · ${l.method}` : ""}</span>
                <span className={`font-mono ${l.type === "credit" ? "text-[#2D5A27]" : "text-[#C25D38]"}`}>
                  {l.type === "credit" ? "+" : "−"}₹{l.amount.toFixed(2)}
                </span>
              </div>
            ))}
        </Section>
      </div>

      {payoutOpen && (
        <PayoutModal max={w.available} customerId={id}
                      onClose={() => setPayoutOpen(false)}
                      onDone={() => { setPayoutOpen(false); load(); }}/>
      )}
    </AppShell>
  );
}

const Stat = ({ label, value, icon: I, accent }) => (
  <div className={`bg-white border ${accent ? "border-[#2D5A27]" : "border-[#E5E2D9]"} rounded-md p-4`}>
    <div className="flex items-center gap-2 mb-2">
      {I && <I size={14} className="text-[#2D5A27]"/>}
      <div className="text-[10px] uppercase tracking-[0.2em] text-[#5C5A55]">{label}</div>
    </div>
    <div className={`text-2xl font-light ${accent ? "text-[#2D5A27]" : "text-[#2D2B26]"}`}>{value}</div>
  </div>
);

const Section = ({ title, testid, count, icon: I, children }) => (
  <div className="bg-white border border-[#E5E2D9] rounded-md overflow-hidden" data-testid={testid}>
    <div className="px-5 py-3 border-b border-[#E5E2D9] flex items-center gap-2">
      {I ? <I size={14}/> : <Users size={14}/>}
      <div className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55] flex-1">{title}</div>
      <span className="text-xs text-[#8A8780]">{count}</span>
    </div>
    <div className="max-h-72 overflow-y-auto">{children}</div>
  </div>
);

const Empty = ({ children }) => (
  <div className="p-6 text-center text-xs text-[#8A8780]">{children}</div>
);

const PayoutModal = ({ max, customerId, onClose, onDone }) => {
  const [f, setF] = useState({
    amount: "", method: "upi", upi_txn_id: "",
    paid_date: new Date().toISOString().slice(0, 10), notes: "",
  });
  const [busy, setBusy] = useState(false);
  const go = async () => {
    const amt = Number(f.amount);
    if (!amt || amt <= 0) { toast.error("Amount must be > 0"); return; }
    if (amt > max) { toast.error(`Cannot exceed ₹${max.toFixed(2)} available`); return; }
    if (f.method === "upi" && !f.upi_txn_id.trim()) { toast.error("UPI txn ID required"); return; }
    setBusy(true);
    try {
      await api.post(`/customers/${customerId}/payout`,
        { ...f, amount: amt, upi_txn_id: f.upi_txn_id || null });
      toast.success("Payout recorded");
      onDone?.();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-md max-w-md w-full p-6" onClick={e => e.stopPropagation()} data-testid="payout-modal">
        <div className="font-heading text-lg mb-2">Manual Payout</div>
        <div className="text-xs text-[#5C5A55] mb-4">Available: <b>₹{max.toFixed(2)}</b></div>

        <L>Method *</L>
        <div className="grid grid-cols-2 gap-2 mb-3">
          {["upi", "cash"].map(m => (
            <button key={m} onClick={() => setF(s => ({ ...s, method: m }))}
                    data-testid={`payout-method-${m}`}
                    className={`h-10 rounded-md border text-xs font-medium ${f.method === m ? "bg-[#2D5A27] border-[#2D5A27] text-white" : "bg-white border-[#E5E2D9] text-[#5C5A55]"}`}>
              {m.toUpperCase()}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <L>Amount *</L>
            <input type="number" value={f.amount} onChange={e => setF(s => ({ ...s, amount: e.target.value }))}
                   placeholder={`Max ₹${max.toFixed(2)}`}
                   data-testid="payout-amount"
                   className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md text-sm font-mono"/>
          </div>
          <div>
            <L>Paid Date *</L>
            <input type="date" value={f.paid_date} onChange={e => setF(s => ({ ...s, paid_date: e.target.value }))}
                   data-testid="payout-date"
                   className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md text-sm"/>
          </div>
        </div>

        {f.method === "upi" && (
          <div className="mt-3">
            <L>UPI Txn ID *</L>
            <input value={f.upi_txn_id} onChange={e => setF(s => ({ ...s, upi_txn_id: e.target.value }))}
                   data-testid="payout-upi-txn"
                   className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md text-sm font-mono"/>
          </div>
        )}

        <L className="mt-3">Notes</L>
        <textarea value={f.notes} onChange={e => setF(s => ({ ...s, notes: e.target.value }))} rows={2}
                  data-testid="payout-notes"
                  className="w-full px-3 py-2 border border-[#E5E2D9] rounded-md text-xs mb-4"/>

        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="h-10 px-4 border border-[#E5E2D9] rounded-md text-sm">Cancel</button>
          <button onClick={go} disabled={busy} data-testid="payout-submit"
                  className="h-10 px-4 bg-[#2D5A27] hover:bg-[#3A7033] disabled:opacity-60 text-white rounded-md text-sm font-medium inline-flex items-center gap-2">
            <Coins size={14} weight="bold"/>{busy ? "Recording…" : "Record Payout"}
          </button>
        </div>
      </div>
    </div>
  );
};

const L = ({ children, className = "" }) => (
  <label className={`text-[11px] uppercase tracking-[0.2em] text-[#5C5A55] block mb-1.5 ${className}`}>{children}</label>
);
